import { GraphQLClient } from 'graphql-request';
import { getSdk } from './graphql-sdk';
import type { PreviewMode } from '@/contexts/SiteContext';

// Enable debug logs only in development
const DEBUG = false; //process.env.NODE_ENV === 'development';

export class GraphQLRequester {
    private client: GraphQLClient;
    private sdk: ReturnType<typeof getSdk>;
    private previewMode: PreviewMode;
    private endpoint: string;
    private debug: boolean;

    constructor(
        endpoint: string = process.env.NEXT_PUBLIC_GRAPHQL_ENDPOINT ||
            'https://haug-craft.ddev.site/api',
        _token: string = process.env.NEXT_PUBLIC_GRAPHQL_TOKEN || '',
        previewMode: PreviewMode = { mode: "none", code: null, token: null },
        debug: boolean = DEBUG
    ) {
        this.endpoint = endpoint;
        this.debug = debug;
        this.previewMode = previewMode;
        
        this.logDebug('GraphQLRequester initialized', { 
            endpoint,
            previewMode 
        });

        this.client = this.createClient();
        this.sdk = getSdk(this.client);
    }

    private createClient(): GraphQLClient {
        const url = new URL(this.endpoint);

        // Clear existing preview parameters
        url.searchParams.delete('x-craft-live-preview');
        url.searchParams.delete('x-craft-preview');
        url.searchParams.delete('token');

        // Add preview parameters if in preview mode
        if (this.previewMode.mode !== "none" && this.previewMode.code) {
            const previewParam = this.previewMode.mode === "live" ? "x-craft-live-preview" : "x-craft-preview";
            url.searchParams.append(previewParam, this.previewMode.code);

            if (this.previewMode.token) {
                url.searchParams.append("token", this.previewMode.token);
            }

            this.logDebug('Added preview parameters to URL', {
                previewParam,
                code: this.previewMode.code,
                token: this.previewMode.token,
                finalUrl: url.toString()
            });
        }

        const headers = {
            Authorization: `Bearer ${process.env.NEXT_PUBLIC_GRAPHQL_TOKEN || ''}`,
            ...(this.previewMode.mode !== "none" ? { "x-craft-gql-cache": "no-cache" } : {}),
        };

        this.logDebug('Creating GraphQL client', {
            url: url.toString(),
            headers,
            previewMode: this.previewMode,
            cache: this.previewMode.mode !== "none" ? "no-store" : undefined
        });

        return new GraphQLClient(url.toString(), {
            headers,
            cache: this.previewMode.mode !== "none" ? "no-store" : undefined,
        });
    }

    // Method to set preview mode
    setPreviewMode(previewMode: PreviewMode) {
        this.logDebug('Setting preview mode', { previewMode });
        this.previewMode = previewMode;
        this.client = this.createClient();
        this.sdk = getSdk(this.client);
    }

    // Helper method to handle errors
    private async handleRequest(request: Promise<any>): Promise<any> {
        try {
            const response = await request;
            this.logDebug('GraphQL request successful', { 
                response,
                previewMode: this.previewMode,
                endpoint: this.endpoint
            });
            return response;
        } catch (error) {
            if (error instanceof Error) {
                this.logDebug('GraphQL request failed', { 
                    error: error.message,
                    previewMode: this.previewMode,
                    endpoint: this.endpoint
                });
                throw new Error(`GraphQL request failed: ${error.message}`);
            }
            throw error;
        }
    }

    // Debug logging helper
    private logDebug(message: string, data?: any) {
        if (this.debug) {
            console.log(`[GraphQL] ${message}`, data);
        }
    }

    // Generic method to execute any SDK method
    async executeSDKMethod(methodName: string, variables?: any): Promise<any> {
        this.logDebug(`Executing SDK method: ${methodName}`, variables);
        
        if (!(methodName in this.sdk)) {
            throw new Error(`Method "${methodName}" not found in SDK. Available methods: ${Object.keys(this.sdk).join(', ')}`);
        }
        
        const method = this.sdk[methodName as keyof typeof this.sdk];
        return this.handleRequest(method(variables));
    }

    // Get the current SDK instance
    getSdk() {
        return this.sdk;
    }

    // Get the current client instance
    getClient() {
        return this.client;
    }
}
